import BigNumber from 'bignumber.js';

import { isChainKey } from '@infinex/chain-config';
import type {
  EscrowSendReceivedItem,
  EscrowSendSentItem,
} from '@infinex/main-service-contract/v2/client/escrowSend';
import type { EscrowSendStatus } from '@infinex/types';

import type {
  PrivateReceiveActivity,
  PrivateSendActivity,
} from '../../../types';
import { ActivityStatus, ActivityType } from '../../../types';

/**
 * Coarse feed status for an escrow send. `unclaimed`/`expired`/`refunded`
 * map to SUCCESS (stable, non-spinning states — the claim state is conveyed
 * by the row badge, not a spinner); only draft/refunding animate.
 */
export const mapEscrowSendStatusToActivityStatus = (
  status: EscrowSendStatus,
  fundingDeadline: string | null,
  now: Date
): ActivityStatus => {
  switch (status) {
    case 'draft':
      // A draft past its funding deadline is provably dead (the deposit
      // address is never re-minted) — render failed instead of "Sending".
      return fundingDeadline && new Date(fundingDeadline) < now
        ? ActivityStatus.ERROR
        : ActivityStatus.PROCESSING;
    case 'refunding':
      return ActivityStatus.PROCESSING;
    case 'error':
      return ActivityStatus.ERROR;
    case 'review':
      return ActivityStatus.REVIEW;
    case 'unclaimed':
    case 'started':
    case 'claimed':
    case 'refunded':
    case 'expired':
      return ActivityStatus.SUCCESS;
  }
};

export const mapToPrivateSendActivity = (
  item: EscrowSendSentItem
): PrivateSendActivity | null => {
  if (!isChainKey(item.chainKey)) return null;
  return {
    id: item.id,
    type: ActivityType.PRIVATE_SEND,
    status: mapEscrowSendStatusToActivityStatus(
      item.status,
      item.fundingDeadline,
      new Date()
    ),
    escrowStatus: item.status,
    recipientIdentityType: item.recipientIdentityType,
    recipientHandleAtSend: item.recipientHandleAtSend,
    custodyBackend: item.custodyBackend,
    chain: item.chainKey,
    assetAddress: item.assetId,
    decimals: item.assetDecimals,
    amount: new BigNumber(item.quotedAmount),
    settledAmount: item.settledAmount
      ? new BigNumber(item.settledAmount)
      : null,
    expiresAt: item.expiresAt ? new Date(item.expiresAt) : null,
    timestamp: new Date(item.createdAt),
  };
};

export const mapToPrivateReceiveActivity = (
  item: EscrowSendReceivedItem
): PrivateReceiveActivity | null => {
  if (!isChainKey(item.chainKey)) return null;
  return {
    // Suffixed so a self-claimed send never collides with its sender row
    // in the id-keyed detail lookup.
    id: `${item.id}-received`,
    type: ActivityType.PRIVATE_RECEIVE,
    status: ActivityStatus.SUCCESS,
    custodyBackend: item.custodyBackend,
    chain: item.chainKey,
    assetAddress: item.assetId,
    decimals: item.assetDecimals,
    amount: new BigNumber(item.settledAmount ?? item.quotedAmount),
    sentAmount: new BigNumber(item.quotedAmount),
    claimDestinationType: item.claimDestinationType,
    claimedToAddress: item.claimedToAddress,
    timestamp: new Date(item.claimedAt),
  };
};

/**
 * Wallet-funded escrow sends run their funding leg through the normal
 * transfers rail with `requestId = escrowSendId`, so the raw transfer also
 * arrives via `getWithdrawals`. Those rows are dropped in favour of the
 * private-send row.
 */
export const isEscrowFundingWithdrawal = (
  withdrawal: { requestId?: string | null },
  escrowSendIds: ReadonlySet<string>
): boolean => !!withdrawal.requestId && escrowSendIds.has(withdrawal.requestId);
